// Header.jsx
import React, { useState, useRef, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from 'services/api'; // Import du service API
import { useAuth } from "contexts/AuthContext";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AccessibilityOutline, BagOutline, PersonOutline } from 'react-ionicons';

import { faMagnifyingGlass, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import BurgerMenu from '../navigation/BurgerMenu';
import Sidebar from '../navigation/Sidebar';
import AccessibilitySidebar from 'components/settings/AccessibilitySidebar';
import buttons from 'components/common/Button.module.css';
import stylesDesktop from './Header.module.css';
import stylesMobile from './HeaderMobile.module.css';
import useAccessibility from 'hooks/useAccessibility';
import { AccessibilitySidebarContext } from 'contexts/AccessibilitySidebarContext';
import useBreakpoint from 'hooks/useBreakpoint';

import { ReactComponent as PersonOutlineIcon } from 'icons/person-outline.svg';
import { useCart } from 'contexts/CartContext';

const Header = (variant) => {
  const { isLoggedIn } = useAuth();
  const { logout } = useAuth();

  const isMobile = useBreakpoint();
  const accessibilityVariant = useAccessibility();

  // États pour les sidebars
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isSubSidebarOpen, setSubSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // États pour la recherche et l'autocomplétion
  const [query, setQuery] = useState('');
  const [isMobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const menuRef = useRef(null);

  const { cartQuantity } = useCart();


  // Contextes pour les sidebars (desktop)
  const { openAccessibilitySidebar } = useContext(AccessibilitySidebarContext);

  // États et handlers pour les sidebars mobiles
  const [isAccessibilitySidebarOpen, setAccessibilitySidebarOpen] = useState(false);

  const navigate = useNavigate();

  // Gestion des sidebars
  useEffect(() => {
    if (isSidebarOpen || isAccessibilitySidebarOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isSidebarOpen, isAccessibilitySidebarOpen]);

  const handleAccessibilityClick = () => {
    if (isMobile) {
      setAccessibilitySidebarOpen(true);
    } else {
      openAccessibilitySidebar();
    }
  };

  const handleCloseAccessibilitySidebar = () => {
    setAccessibilitySidebarOpen(false);
  };

  const handleBurgerClick = () => {
    setSidebarOpen(true);
  };

  const handleCloseAllSidebars = () => {
    setSidebarOpen(false);
    setSelectedItem(null);
    if (menuRef.current) {
      menuRef.current.focus();
    }
  };

  const handleCloseSidebar = () => {
    setSidebarOpen(false);
    setSelectedItem(null);
    if (menuRef.current) {
      menuRef.current.focus();
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setSubSidebarOpen(true);
  };

  // Sélection des styles en fonction du contexte
  const styles = isMobile ? stylesMobile : stylesDesktop;

  // Sélection du composant Menu en fonction du contexte
  const MenuComponent = BurgerMenu; // Utiliser BurgerMenu

  return (
    <header className={`${styles.header} ${styles[variant]} ${styles[accessibilityVariant]}`}>
      <div className={styles.headerContent}>
        <div className={styles.heading}>
          <MenuComponent onClick={handleBurgerClick} ref={menuRef} />
          <p className={styles.brand}>
            Arras {'>'}
          </p>
          <Link className={styles.brandSubSite} to="/" aria-label="Retour à l'accueil Ville d'Arras">
            Billetterie le Pharos
          </Link>

        </div>
        {!isMobile && (
          <>
            {/* Bouton d'accessibilité */}
            <button
              className={`${buttons.primary} ${buttons.btnOutline} ${styles.accessButton}`}
              onClick={handleAccessibilityClick}
              aria-label="Paramètres d'accessibilité"
              title="Paramètres d'accessibilité"
            >
              <AccessibilityOutline
                height=""
                width=""
                color="currentColor"
                className={`${styles.icon} ${styles.accessibilityIcon}`}
              />
              <span className={styles.buttonLabel}>Accessibilité</span>
            </button>


            <Link className={`${buttons.primary} ${buttons.light} ${styles.cartButton}`} to="/panier">
              <BagOutline
                height=""
                width=""
                color="currentColor"
                className={`${styles.icon} ${styles.light} ${styles.accessibilityIcon}`}
              />
              <span className={styles.buttonLabel}>Panier {cartQuantity > 0 ? `(${cartQuantity})` : ''}</span>
            </Link>

            <Link className={`${buttons.primary} ${styles.accountButton}`} to={isLoggedIn ? "/mon-compte" : "/login"}>
              <PersonOutline
                height=""
                width=""
                color="currentColor"
                className={`${styles.icon} ${styles.light} ${styles.accessibilityIcon}`}
              />
              <span className={styles.buttonLabel}>Mon compte</span>
            </Link>
          </>
        )}
      </div>

      {/* Sidebar principale */}
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        onItemSelect={handleItemSelect}
        selectedItem={selectedItem}
        onCloseAllSidebars={handleCloseAllSidebars}
      />

      {/* Sidebars spécifiques au mobile */}
      {isMobile && isAccessibilitySidebarOpen && (
        <AccessibilitySidebar
          onClose={handleCloseAccessibilitySidebar}
          onSave={(options) => console.log("Options d'accessibilité enregistrées:", options)}
        />
      )}


      {/*{isLoggedIn && (*/}
      {/*    <button onClick={logout}>Déconnexion</button>*/}
      {/*)}*/}

    </header>
  );
};

export default Header;
